import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Select,
  Stack,
  Textarea,
  Text,
  useToast,
  VStack,
  ScaleFade,
  Badge,
  Progress,
  AspectRatio,
  HStack,
  Input,
  Image as ChakraImage,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  useDisclosure,
  IconButton,
  Skeleton,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaDownload, FaExpand, FaChevronLeft, FaChevronRight, FaPlay, FaShare } from 'react-icons/fa';
import useAuth from '../store/useAuth';
import useCredits from '../store/useCredits';
import useCreations from '../store/useCreations';
import { COLORS, VIDEO_SIZES, VIDEO_DURATIONS, CREDIT_COSTS } from '../utils/constants';
import { generateVideo } from '../services/AIService';
import { fetchGenerationHistory, mapGenerationToViewModel } from '../services/GenerationService';
import RecentHistoryGrid from '../components/RecentHistoryGrid';

// Motion components
const MotionBox = motion(Box);
const MotionButton = motion(Button);

// Loading dots animation
const LoadingDots = () => (
  <span className="loading-dots">
    <span></span>
    <span></span>
    <span></span>
  </span>
);

// Video loading spinner
const VideoSkeleton = () => (
  <MotionBox
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
    h="320px"
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"

    borderRadius="md"
  >
    <VStack spacing={4}>
      <Box
        w="40px"
        h="40px"
        border="3px solid"
        borderColor={COLORS.primary}
        borderRadius="full"
        borderTopColor="transparent"
        animation="spin 1s linear infinite"
        sx={{
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' }
          }
        }}
      />
      <Text 
        color={COLORS.primary} 
        fontSize="md" 
        fontWeight="medium"
        textAlign="center"
      >
        Generating…
      </Text>
    </VStack>
  </MotionBox>
);

function VideoGenerate() {
  const [prompt, setPrompt] = useState('');
  const [videoSize, setVideoSize] = useState('1280:720');
  const [duration, setDuration] = useState(5);
  const [promptImage, setPromptImage] = useState('');
  const [uploadedImageFile, setUploadedImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [generatedVideo, setGeneratedVideo] = useState('');
  const [currentGeneratedVideo, setCurrentGeneratedVideo] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [recentVideos, setRecentVideos] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [availableImages, setAvailableImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  
  const { isOpen: isLightboxOpen, onOpen: onLightboxOpen, onClose: onLightboxClose } = useDisclosure();
  const { isOpen: isImageSelectorOpen, onOpen: onImageSelectorOpenBase, onClose: onImageSelectorClose } = useDisclosure();
  
  const navigate = useNavigate();
  const toast = useToast();
  
  const { user, isLoggedIn } = useAuth();

  // Load from database when user is available
  useEffect(() => {
    async function loadRecentVideos() {
      if (user?.walletAddress) {
        setIsLoadingHistory(true);
        try {
          const result = await fetchGenerationHistory(user.walletAddress, 12, 0);
          if (result.success) {
            const videoGenerations = result.generations
              .filter(gen => gen.generation_type === 'video')
              .map(mapGenerationToViewModel);
            setRecentVideos(videoGenerations);
            
            // Optional: Cache for offline mode
            try {
              localStorage.setItem(`recent_videos_cache_${user.walletAddress}`, JSON.stringify(videoGenerations));
            } catch (e) {
              console.warn('Failed to cache recent videos:', e.message);
            }
          } else {
            console.error('Failed to load recent videos:', result.error);
            // Fallback to cached data only if API fails
            try {
              const cached = localStorage.getItem(`recent_videos_cache_${user.walletAddress}`);
              if (cached) {
                setRecentVideos(JSON.parse(cached));
              }
            } catch (e) {
              console.warn('Failed to load cached videos:', e.message);
            }
          }
        } catch (error) {
          console.error('Error loading recent videos:', error);
        } finally {
          setIsLoadingHistory(false);
        }
      }
    }
    
    loadRecentVideos();
  }, [user?.walletAddress]);

  // Load available images from Image generation page
  // Note: Due to localStorage quota management, recent images now only store metadata
  // Only images passed via 'selected_image_for_video' contain full image URLs
  useEffect(() => {
    if (user?.walletAddress) {
      try {
        const savedImages = localStorage.getItem(`recent_images_${user.walletAddress}`);
        if (savedImages) {
          const imageMetadata = JSON.parse(savedImages);
          // Since images now only store metadata, we can't display them in the selector
          // Users need to use the "Use for Video" button directly from Image page
          setAvailableImages([]); // No images available for selection from history
        }
      } catch (error) {
        console.error('Error loading recent images:', error);
      }
    }
  }, [user?.walletAddress]);

  // Remove localStorage saving logic - data comes from database
  const { balance, deductCredits } = useCredits();
  const { addCreation } = useCreations();
  
  // Handle opening image selector - fetch fresh data
  const onImageSelectorOpen = async () => {
    setIsLoadingImages(true);
    onImageSelectorOpenBase();
    
    try {
      console.log('🔍 [IMAGE_SELECTOR] Fetching generation history for wallet:', user.walletAddress);
      
      if (!user?.walletAddress) {
        console.error('❌ [IMAGE_SELECTOR] No wallet address available');
        setAvailableImages([]);
        return;
      }
      
      const result = await fetchGenerationHistory(user.walletAddress, 40, 0);
      console.log('📋 [IMAGE_SELECTOR] Generation history result:', {
        success: result.success,
        error: result.error,
        generationsCount: result.generations?.length || 0,
        firstFewGenerations: result.generations?.slice(0, 3)
      });
      
      if (result.success && result.generations) {
        console.log('📊 [IMAGE_SELECTOR] Total generations found:', result.generations.length);
        
        // Debug each generation before filtering
        result.generations.forEach((gen, index) => {
          console.log(`🔍 [IMAGE_SELECTOR] Gen ${index}:`, {
            id: gen.id,
            type: gen.generation_type,
            hasUrl: !!gen.result_url,
            created: gen.created_at
          });
        });
        
        const imageGenerations = result.generations
          .filter(gen => {
            const isImage = gen.generation_type === 'image';
            const hasUrl = !!gen.result_url;
            console.log(`🔍 [IMAGE_SELECTOR] Filtering gen ${gen.id}: type=${gen.generation_type}, isImage=${isImage}, hasUrl=${hasUrl}`);
            return isImage && hasUrl;
          })
          .map(gen => {
            const mapped = mapGenerationToViewModel(gen);
            console.log(`🔄 [IMAGE_SELECTOR] Mapped generation:`, {
              id: mapped.id,
              url: mapped.url,
              prompt: mapped.prompt?.substring(0, 50) + '...'
            });
            return mapped;
          });
          
        console.log('✅ [IMAGE_SELECTOR] Filtered image generations:', imageGenerations.length);
        console.log('📋 [IMAGE_SELECTOR] Sample images:', imageGenerations.slice(0, 2));
        setAvailableImages(imageGenerations);
      } else {
        console.error('❌ [IMAGE_SELECTOR] Failed to load recent images from API:', result.error);
        
        // Fallback to localStorage if API fails
        console.log('🔄 [IMAGE_SELECTOR] Trying localStorage fallback...');
        try {
          const savedImages = localStorage.getItem(`recent_images_${user.walletAddress}`);
          if (savedImages) {
            const imageData = JSON.parse(savedImages);
            console.log('📦 [IMAGE_SELECTOR] Found localStorage images:', imageData.length);
            setAvailableImages(imageData);
          } else {
            console.log('📦 [IMAGE_SELECTOR] No localStorage images found');
            setAvailableImages([]);
          }
        } catch (localError) {
          console.error('❌ [IMAGE_SELECTOR] localStorage error:', localError);
          setAvailableImages([]);
        }
      }
    } catch (error) {
      console.error('💥 [IMAGE_SELECTOR] Error loading recent images:', error);
      
      // Try localStorage as final fallback
      try {
        const savedImages = localStorage.getItem(`recent_images_${user.walletAddress}`);
        if (savedImages) {
          const imageData = JSON.parse(savedImages);
          console.log('📦 [IMAGE_SELECTOR] Using localStorage fallback:', imageData.length);
          setAvailableImages(imageData);
        } else {
          setAvailableImages([]);
        }
      } catch (localError) {
        console.error('❌ [IMAGE_SELECTOR] Final fallback failed:', localError);
        setAvailableImages([]);
      }
    } finally {
      setIsLoadingImages(false);
    }
  };
  
  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  // Fetch credits when user is available
  useEffect(() => {
    if (user && user.walletAddress) {
      const { fetchBalance } = useCredits.getState();
      fetchBalance(user.walletAddress);
    }
  }, [user]);

  // Check for pre-selected image from Image generation page
  useEffect(() => {
    const selectedImage = localStorage.getItem('selected_image_for_video');
    if (selectedImage) {
      try {
        const imageData = JSON.parse(selectedImage);
        setPromptImage(imageData.url);
        setImagePreview(imageData.url);
        setPrompt(imageData.prompt || ''); // Pre-fill prompt if available
        
        // Clear the stored selection
        localStorage.removeItem('selected_image_for_video');
        
        toast({
          title: 'Image Loaded',
          description: 'Selected image has been loaded for video generation',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error loading selected image:', error);
      }
    }
  }, []);

  // Simulate generation progress
  useEffect(() => {
    let interval;
    if (isGenerating) {
      interval = setInterval(() => {
        setGenerationProgress(prev => {
          const nextProgress = prev + Math.random() * 5;
          if (nextProgress >= 100) {
            clearInterval(interval);
            return 100;
          }
          return nextProgress;
        });
      }, 1000);
    } else {
      setGenerationProgress(0);
    }
    return () => clearInterval(interval);
  }, [isGenerating]);

  // Handle image file upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload an image file',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please upload an image smaller than 10MB',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setUploadedImageFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setPromptImage(e.target.result); // Set base64 data for API
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove uploaded image
  const removeImage = () => {
    setUploadedImageFile(null);
    setImagePreview('');
    setPromptImage('');
  };
  
  // Get credit cost based on video length and size
  const getCreditCost = () => {
    // Use exact credit costs based on duration - no multipliers
    const durationOption = VIDEO_DURATIONS.find(d => d.value === duration);
    if (durationOption && durationOption.creditCost) {
      return durationOption.creditCost;
    }
    // Default fallback 
    return duration === 10 ? 240 : 120;
  };

  // Handle lightbox navigation
  const openLightbox = (index) => {
    setSelectedVideoIndex(index);
    onLightboxOpen();
  };

  const navigateLightbox = (direction) => {
    const newIndex = direction === 'next' 
      ? (selectedVideoIndex + 1) % recentVideos.length
      : (selectedVideoIndex - 1 + recentVideos.length) % recentVideos.length;
    setSelectedVideoIndex(newIndex);
  };
  
  const handleGenerateVideo = async () => {
    if (prompt.trim() === '' || !promptImage) {
      toast({
        title: 'Error',
        description: 'Please provide both an image and video description',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Check if user has enough credits
    if (balance < getCreditCost()) {
      toast({
        title: 'Insufficient Credits',
        description: `You need ${getCreditCost()} credits to generate a video. You have ${balance} credits.`,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    
    setIsGenerating(true);
    setGeneratedVideo('');
    setCurrentGeneratedVideo(null);
    setGenerationProgress(0);
    
    try {
      // Deduct credits first
      await deductCredits(user.walletAddress, getCreditCost(), 'usage', `vid_${Date.now()}`);

      // Trigger backend video generation
      const result = await generateVideo({ 
        promptText: prompt, 
        promptImage: promptImage, // This is now base64 data
        ratio: videoSize, 
        duration 
      }, user.walletAddress);

      setGeneratedVideo(result.videoUrl);

      // Add to recent videos with proper view model structure
      const resultViewModel = {
        id: result.taskId || `temp_${Date.now()}`, // Use taskId from result or temporary ID
        url: result.videoUrl,
        prompt: prompt,
        type: 'video',
        created_at: new Date().toISOString(),
        dimensions: videoSize,
        style: 'n/a',
        metadata: {
          taskId: result.taskId,
          duration: duration,
          ratio: videoSize
        },
        timestamp: Date.now()
      };
      
      // Set current generated video for button actions
      setCurrentGeneratedVideo(resultViewModel);
      
      setRecentVideos(prev => {
        const list = [resultViewModel, ...prev];
        // Cache the updated list
        try {
          localStorage.setItem(`recent_videos_cache_${user.walletAddress}`, JSON.stringify(list.slice(0, 40)));
        } catch (e) {
          console.warn('Failed to cache recent videos:', e.message);
        }
        return list;
      });

      // Save to creations
      addCreation({
        url: result.videoUrl,
        prompt: prompt,
        parameters: { ratio: videoSize, duration },
        type: 'video',
        timestamp: result.timestamp,
        size: videoSize,
        creditCost: getCreditCost()
      });

      toast({
        title: 'Video Generated',
        description: 'Your video has been generated successfully!',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Video generation failed:", error);
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate video. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsGenerating(false);
      setGenerationProgress(100);
    }
  };
  
  const handleViewCreations = () => {
    navigate('/gallery');
  };

  // Handler for downloading a video
  const handleDownload = (video, index) => {
    if (!video || !video.url) {
      toast({
        title: 'Download failed',
        description: 'Video URL is not available',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = video.url;
      link.download = `ai-video-${video.timestamp || Date.now()}.mp4`;
      link.target = '_blank'; // Fallback for CORS issues
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Download started',
        description: 'Your video download has begun',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: open video in new tab
      window.open(video.url, '_blank');
      toast({
        title: 'Download method changed',
        description: 'Video opened in new tab. Right-click to save.',
        status: 'info',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Handler for sharing a video
  const handleShare = async (video, index) => {
    if (!video || !video.url) {
      toast({
        title: 'Share failed',
        description: 'Video URL is not available',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if Web Share API is supported
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'AI Generated Video',
          text: `Check out this AI-generated video: "${video.prompt}"`,
          url: video.url,
        });
        
        toast({
          title: 'Shared successfully',
          description: 'Video shared successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share error:', error);
          // Fallback to clipboard
          fallbackShare(video);
        }
      }
    } else {
      // Fallback for browsers without Web Share API
      fallbackShare(video);
    }
  };

  // Fallback share method using clipboard
  const fallbackShare = async (video) => {
    try {
      await navigator.clipboard.writeText(video.url);
      toast({
        title: 'Link copied',
        description: 'Video link copied to clipboard',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Clipboard error:', error);
      // Final fallback: show the URL in a prompt
      const shareText = `AI Generated Video: "${video.prompt}"\n\n${video.url}`;
      if (window.prompt) {
        window.prompt('Copy this link to share:', video.url);
      } else {
        toast({
          title: 'Share link ready',
          description: 'Please copy the URL from the address bar',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        window.open(video.url, '_blank');
      }
    }
  };
  
  return (
    <Container maxW="6xl" py={{ base: 8, md: 12 }} px={{ base: 4, md: 6 }}>
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <VStack spacing={{ base: 4, md: 8 }} align="stretch">
          <Flex 
            justify="space-between" 
            align="center"
            direction={{ base: 'column', sm: 'row' }}
            gap={{ base: 3, sm: 0 }}
          >
            <Flex align="center">
              <Heading 
                as="h1" 
                size={{ base: 'lg', md: 'xl' }}
                color="white"
                letterSpacing="tight"
                fontWeight="bold"
              >
                AI Video Generator
              </Heading>
            </Flex>
            
            <Badge 
              px={{ base: 3, md: 4 }}
              py={{ base: 1.5, md: 2 }}
              borderRadius="md" 
              fontWeight="bold"
              bg="rgba(30, 220, 11, 0.15)"
              border="1px solid"
              borderColor={COLORS.primary}
              color={COLORS.accent}
              className="pulse"
              fontSize={{ base: 'xs', md: 'sm' }}
            >
              Credits: {balance}
            </Badge>
          </Flex>
          
          <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 4, md: 6 }}>
            {/* Left side: Form */}
            <Box 
              flex="1" 
              p={{ base: 4, md: 6 }}
              borderRadius="lg"
              border="2px dashed"
              borderColor="rgb(7, 74, 0)"
              background="linear-gradient(135deg, rgba(0, 20, 5, 0.8) 0%, rgba(0, 40, 10, 0.6) 100%)"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
              backdropFilter="blur(10px)"
              position="relative"
              zIndex="1"
              transition="all 0.3s ease"
              _hover={{
                borderColor: "rgba(30, 220, 11, 0.6)",
                background: "linear-gradient(135deg, rgba(0, 30, 8, 0.9) 0%, rgba(0, 50, 15, 0.7) 100%)",
                transform: "translateY(-2px)",
                boxShadow: "0 12px 48px rgba(30, 220, 11, 0.2)"
              }}
            >
              <Stack spacing={5}>
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel color={COLORS.textSecondary}>Starting Image</FormLabel>
                    {!imagePreview ? (
                      <VStack spacing={3}>
                        {/* Upload Option */}
                      <Box
                        borderWidth="2px"
                        borderStyle="dashed"
                        borderColor="rgba(30, 220, 11, 0.3)"
                        borderRadius="md"
                          p={4}
                        textAlign="center"
                        _hover={{ borderColor: COLORS.accent }}
                        cursor="pointer"
                        onClick={() => document.getElementById('image-upload').click()}
                          w="100%"
                      >
                        <Input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          display="none"
                        />
                          <Text color={COLORS.textSecondary} mb={1} fontWeight="medium">
                            📁 Upload New Image
                        </Text>
                        <Text fontSize="sm" color={COLORS.textSecondary} opacity={0.7}>
                            Click to upload from your device
                        </Text>
                      </Box>

                        {/* Divider */}
                        <HStack w="100%">
                          <Box flex="1" h="1px" bg="rgba(30, 220, 11, 0.2)" />
                          <Text color={COLORS.textSecondary} fontSize="sm" px={3}>OR</Text>
                          <Box flex="1" h="1px" bg="rgba(30, 220, 11, 0.2)" />
                        </HStack>

                        {/* Recent Images Option */}
                        <Box
                          borderWidth="2px"
                          borderStyle="dashed"
                          borderColor="rgba(59, 130, 246, 0.3)"
                          borderRadius="md"
                          p={4}
                          textAlign="center"
                          _hover={{ borderColor: "rgba(59, 130, 246, 0.6)" }}
                          cursor="pointer"
                          onClick={onImageSelectorOpen}
                          w="100%"
                          bg="rgba(59, 130, 246, 0.05)"
                        >
                          <Text color="blue.300" mb={1} fontWeight="medium">
                            Choose from Recent Images
                          </Text>
                          <Text fontSize="sm" color="blue.200" opacity={0.8}>
                            Use "Use for Video" button on Image page instead
                          </Text>
                        </Box>
                      </VStack>
                    ) : (
                      <Box position="relative">
                        <ChakraImage
                          src={imagePreview}
                          alt="Uploaded image"
                          maxH="200px"
                          objectFit="contain"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="rgba(30, 220, 11, 0.3)"
                        />
                        <Button
                          position="absolute"
                          top={2}
                          right={2}
                          size="sm"
                          colorScheme="red"
                          onClick={removeImage}
                        >
                          Remove
                        </Button>
                      </Box>
                    )}
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel color={COLORS.textSecondary}>Video Description (describe the animation)</FormLabel>
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Describe the motion and action you want to happen in the video..."
                      size="lg"
                      rows={4}
                      bg="rgba(0, 0, 0, 0.2)"
                      border="1px solid"
                      borderColor="rgba(30, 220, 11, 0.3)"
                      _hover={{ borderColor: COLORS.accent }}
                      _focus={{ 
                        borderColor: COLORS.accent,
                        boxShadow: `0 0 0 1px ${COLORS.accent}`
                      }}
                      color={COLORS.text}
                      resize="none"
                    />
                  </FormControl>
                </VStack>

                <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                  {/* <FormControl flex="1">
                    <FormLabel color={COLORS.textSecondary} fontSize={{ base: 'sm', md: 'md' }}>
                      Video Size
                    </FormLabel>
                    <Select 
                      value={videoSize} 
                      onChange={(e) => setVideoSize(e.target.value)}
                      bg="rgba(0, 0, 0, 0.2)"
                      border="1px solid"
                      borderColor="rgba(30, 220, 11, 0.3)"
                      _hover={{ borderColor: COLORS.accent }}
                      _focus={{ 
                        borderColor: COLORS.accent,
                        boxShadow: `0 0 0 1px ${COLORS.accent}`
                      }}
                      color={COLORS.text}
                      fontSize={{ base: 'sm', md: 'md' }}
                    >
                      {VIDEO_SIZES.map((size) => (
                        <option key={size.value} value={size.value} style={{background: COLORS.background}}>
                          {size.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl> */}
                  
                  <FormControl flex="1">
                    <FormLabel color={COLORS.textSecondary} fontSize={{ base: 'sm', md: 'md' }}>Duration</FormLabel>
                    <Select 
                      value={duration} 
                      onChange={(e) => setDuration(Number(e.target.value))}
                      bg="rgba(0, 0, 0, 0.2)"
                      border="1px solid"
                      borderColor="rgba(30, 220, 11, 0.3)"
                      _hover={{ borderColor: COLORS.accent }}
                      _focus={{ 
                        borderColor: COLORS.accent,
                        boxShadow: `0 0 0 1px ${COLORS.accent}`
                      }}
                      color={COLORS.text}
                      fontSize={{ base: 'sm', md: 'md' }}
                    >
                      {VIDEO_DURATIONS.map((d) => (
                        <option key={d.value} value={d.value} style={{background: COLORS.background}}>
                          {d.label} 
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                
                <MotionButton
                  mt={4}
                  height="60px"
                  fontSize="lg"
                  onClick={handleGenerateVideo}
                  isLoading={isGenerating}
                  loadingText={<span>Generating <LoadingDots /></span>}
                  className="primary-button"
                  position="relative"
                  overflow="hidden"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                  disabled={isGenerating || balance < getCreditCost() || !promptImage || !prompt.trim()}
                >
                  Generate Video ({getCreditCost()} credits)
                </MotionButton>
              </Stack>
            </Box>
            
            {/* Right side: Preview */}
            <Box 
              flex="1" 
              p={{ base: 4, md: 6 }}
              borderRadius="lg"
              border="2px dashed"
              borderColor="rgb(7, 74, 0)"
              background="linear-gradient(135deg, rgba(0, 20, 5, 0.8) 0%, rgba(0, 40, 10, 0.6) 100%)"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
              backdropFilter="blur(10px)"
              position="relative"
              overflow="hidden"
              zIndex="1"
              transition="all 0.3s ease"
              _hover={{
                borderColor: "rgba(30, 220, 11, 0.6)",
                background: "linear-gradient(135deg, rgba(0, 30, 8, 0.9) 0%, rgba(0, 50, 15, 0.7) 100%)",
                transform: "translateY(-2px)",
                boxShadow: "0 12px 48px rgba(30, 220, 11, 0.2)"
              }}
            >
              <Flex justify="space-between" align="center" mb={4}>
                <Text 
                  fontWeight="bold" 
                  color={COLORS.text}
                  textShadow="0 0 5px rgba(30, 220, 11, 0.5)"
                >
                  {generatedVideo ? 'Generated Video' : 'Preview Area'}
                </Text>
                
                {generatedVideo && (
                  <HStack spacing={2}>
                    <Badge colorScheme="green" variant="subtle" px={2}>
                      {videoSize}
                    </Badge>
                    <Badge colorScheme="green" variant="subtle" px={2}>
                      {duration}s
                    </Badge>
                  </HStack>
                )}
              </Flex>
              
              <MotionBox 
                borderRadius="md" 
                overflow="hidden"
                borderWidth="1px"
                borderColor="rgba(30, 220, 11, 0.2)"
                position="relative"
                whileHover={{ borderColor: "rgba(30, 220, 11, 0.4)" }}
                transition={{ duration: 0.3 }}
              >
                <AnimatePresence mode="wait">
                  {isGenerating ? (
                    <VideoSkeleton key="skeleton" />
                  ) : generatedVideo ? (
                    <MotionBox
                      key="video"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                  <AspectRatio ratio={1}>
                    <video 
                      src={generatedVideo} 
                      controls 
                      autoPlay
                      style={{objectFit: "cover", borderRadius: "md"}}
                          onClick={() => openLightbox(0)}
                    />
                  </AspectRatio>
                    </MotionBox>
                ) : (
                    <MotionBox 
                      key="placeholder"
                    h="320px" 

                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box textAlign="center" p={4}>
                        <Text color={COLORS.textSecondary} mb={3}>
                          Your generated video will appear here
                        </Text>
                        <Box 
                          width="120px" 
                          height="120px" 
                          mx="auto"
                          position="relative"
                          opacity="0.5"
                        >
                          <Box 
                            position="absolute"
                            width="60px"
                            height="60px"
                            top="30px"
                            left="30px"
                            border="2px dashed"
                            borderColor={COLORS.primary}
                            borderRadius="md"
                            zIndex="2"
                          />
                          <Box 
                            position="absolute"
                            width="60px"
                            height="60px"
                            top="20px"
                            left="40px"
                            border="2px dashed"
                            borderColor={COLORS.primary}
                            borderRadius="md"
                            opacity="0.7"
                            zIndex="1"
                          />
                          <Box 
                            position="absolute"
                            width="60px"
                            height="60px"
                            top="40px"
                            left="20px"
                            border="2px dashed"
                            borderColor={COLORS.primary}
                            borderRadius="md"
                            opacity="0.4"
                            zIndex="0"
                          />
                        </Box>
                      </Box>
                    </MotionBox>
                    )}
                </AnimatePresence>
              </MotionBox>
              
              {generatedVideo && (
                <VStack mt={4} spacing={3} align="stretch">
                  <Wrap spacing={{ base: 2, md: 3 }} justify="center">
                    <WrapItem>
                      <Button
                        variant="ghost"
                        size={{ base: 'xs', md: 'sm' }}
                        borderColor={COLORS.primary}
                        color={COLORS.primary}
                        _hover={{
                          bg: "rgba(30, 220, 11, 0.1)",
                        }}
                        onClick={() => handleDownload(currentGeneratedVideo)}
                      >
                        Download
                      </Button>
                    </WrapItem>
                    
                    <WrapItem>
                      <Button
                        variant="ghost"
                        size={{ base: 'xs', md: 'sm' }}
                        borderColor={COLORS.primary}
                        color={COLORS.primary}
                        _hover={{
                          bg: "rgba(30, 220, 11, 0.1)",
                        }}
                        onClick={() => handleShare(currentGeneratedVideo)}
                      >
                        Share
                      </Button>
                    </WrapItem>
                    
                    <WrapItem>
                      <Button
                        variant="ghost"
                        size={{ base: 'xs', md: 'sm' }}
                        borderColor={COLORS.primary}
                        color={COLORS.primary}
                        _hover={{
                          bg: "rgba(30, 220, 11, 0.1)",
                        }}
                        onClick={handleViewCreations}
                      >
                        View All
                      </Button>
                    </WrapItem>
                  </Wrap>
                </VStack>
              )}

              {/* Futuristic blinking dots in the corner */}
              <Box position="absolute" bottom="10px" right="10px">
                <Flex>
                  <Box 
                    w="6px" 
                    h="6px" 
                    borderRadius="full" 
                    bg={COLORS.primary} 
                    mr="4px"
                    opacity={isGenerating ? "1" : "0.4"}
                    sx={
                      isGenerating ? {
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%": { boxShadow: "0 0 0 0 rgba(30, 220, 11, 0.4)" },
                          "70%": { boxShadow: "0 0 0 10px rgba(30, 220, 11, 0)" },
                          "100%": { boxShadow: "0 0 0 0 rgba(30, 220, 11, 0)" }
                        }
                      } : {}
                    }
                  />
                  <Box 
                    w="6px" 
                    h="6px" 
                    borderRadius="full" 
                    bg={COLORS.accent} 
                    opacity={generatedVideo ? "1" : "0.4"}
                  />
                </Flex>
              </Box>
            </Box>
          </Flex>

          {/* Recent Videos Grid */}
          {recentVideos.length > 0 && (
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="recent-videos-section"
            >
              <Heading size="md" color={COLORS.text} mb={4}>
                Recent Video Generations
              </Heading>
              {isLoadingHistory ? (
                <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Skeleton key={index} height="180px" borderRadius="md" />
                  ))}
                </SimpleGrid>
              ) : (
                <RecentHistoryGrid 
                  items={recentVideos} 
                  limit={12} 
                  onItemSelect={(item) => {
                    // Find the index of the clicked video in recentVideos array
                    const videoIndex = recentVideos.findIndex(video => video.id === item.id);
                    if (videoIndex !== -1) {
                      openLightbox(videoIndex);
                    }
                  }}
                />
              )}
            </MotionBox>
          )}
        </VStack>
      </MotionBox>

      {/* Image Selector Modal */}
      <Modal isOpen={isImageSelectorOpen} onClose={onImageSelectorClose} size={{ base: 'full', md: '4xl' }} isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.8)" backdropFilter="blur(10px)" />
        <ModalContent 
          bg={COLORS.background} 
          borderWidth="1px" 
          borderColor={COLORS.primary}
          m={{ base: 0, md: 'auto' }}
          borderRadius={{ base: 0, md: 'md' }}
        >
          <ModalHeader color={COLORS.text} borderBottomWidth="1px" borderColor="rgba(30, 220, 11, 0.2)">
            <HStack spacing={3}>
              <Text>Choose from Recent Images</Text>
              {!isLoadingImages && (
                <Badge colorScheme="green" variant="subtle">
                  {/* {availableImages.length} available */}
                </Badge>
              )}
              {isLoadingImages && (
                <Badge colorScheme="blue" variant="subtle">
                  {/* Loading... */}
                </Badge>
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={COLORS.text} />
          <ModalBody p={6}>
            {isLoadingImages ? (
              <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }} gap={4} maxH="60vh" overflowY="auto">
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton key={index} height="120px" borderRadius="md" />
                ))}
              </Grid>
            ) : availableImages.length === 0 ? (
              <VStack spacing={4} py={8}>
                <Text color={COLORS.textSecondary} fontSize="lg" textAlign="center">
                  🖼️ No Recent Images Found
                </Text>
                <Text color={COLORS.textSecondary} fontSize="sm" textAlign="center" maxW="400px">
                  Generate some images first, then you can use them for video creation.
                </Text>
                <Button
                  colorScheme="green"
                  variant="outline"
                  onClick={() => {
                    navigate('/image');
                    onImageSelectorClose();
                  }}
                >
                  Go to Image Generation
                </Button>
              </VStack>
            ) : (
              <Box maxH="60vh" overflowY="auto">
                <RecentHistoryGrid 
                  items={availableImages} 
                  limit={40}
                  onItemSelect={(item) => {
                    setPromptImage(item.url);
                    setImagePreview(item.url);
                    setPrompt(item.prompt || prompt);
                    setUploadedImageFile(null);
                    onImageSelectorClose();
                    
                    toast({
                      title: 'Image Selected',
                      description: 'Recent image has been loaded for video generation',
                      status: 'success',
                      duration: 3000,
                      isClosable: true,
                    });
                  }}
                />
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Lightbox Modal for Videos */}
      <Modal isOpen={isLightboxOpen} onClose={onLightboxClose} size={{ base: 'full', md: '6xl' }} isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.9)" backdropFilter="blur(10px)" />
        <ModalContent 
          bg="transparent" 
          boxShadow="none" 
          maxW={{ base: '100vw', md: '90vw' }}
          maxH={{ base: '100vh', md: '90vh' }}
          m={{ base: 0, md: 'auto' }}
        >
          <ModalCloseButton 
            color="white" 
            size={{ base: 'md', md: 'lg' }}
            top={{ base: 2, md: 4 }}
            right={{ base: 2, md: 4 }}
            zIndex={10} 
          />
          <ModalBody p={0}>
            {recentVideos[selectedVideoIndex] && (
              <>
                {/* Mobile Layout: Stack elements vertically */}
                <VStack 
                  display={{ base: 'flex', md: 'none' }}
                  spacing={4}
                  h="100vh"
                  justify="center"
                  px={4}
                  py={16}
                >
                  {/* Mobile: Prompt above video */}
                  <Box
                    bg="rgba(0, 0, 0, 0.8)"
                    p={3}
                    borderRadius="md"
                    w="full"
                    maxW="400px"
                  >
                    <Text color="white" fontSize="sm" fontWeight="bold" mb={1} textAlign="center">
                      {recentVideos[selectedVideoIndex].prompt}
                    </Text>
                  </Box>

                  {/* Mobile: Video container with navigation */}
                  <Box position="relative" w="full" maxW="90vw">
                    <video
                      src={recentVideos[selectedVideoIndex].url}
                      controls
                      autoPlay
                      preload="metadata"
                      playsInline
                      style={{ 
                        width: "100%",
                        maxHeight: "50vh",
                        borderRadius: "8px",
                        backgroundColor: "#000",
                        display: "block"
                      }}
                      onError={(e) => console.error('Video error:', e)}
                      onLoadStart={() => console.log('Video loading started')}
                      onLoadedData={() => console.log('Video loaded successfully')}
                    />
                    
                    {/* Mobile Navigation buttons */}
                    {recentVideos.length > 1 && (
                      <>
                        <IconButton
                          position="absolute"
                          left={2}
                          top="50%"
                          transform="translateY(-50%)"
                          icon={<FaChevronLeft />}
                          onClick={() => navigateLightbox('prev')}
                          bg="rgba(0, 0, 0, 0.7)"
                          color="white"
                          _hover={{ bg: "rgba(0, 0, 0, 0.9)" }}
                          size="md"
                          aria-label="Previous video"
                        />
                        <IconButton
                          position="absolute"
                          right={2}
                          top="50%"
                          transform="translateY(-50%)"
                          icon={<FaChevronRight />}
                          onClick={() => navigateLightbox('next')}
                          bg="rgba(0, 0, 0, 0.7)"
                          color="white"
                          _hover={{ bg: "rgba(0, 0, 0, 0.9)" }}
                          size="md"
                          aria-label="Next video"
                        />
                      </>
                    )}
                  </Box>

                  {/* Mobile: Action buttons below video */}
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FaDownload />}
                      onClick={() => handleDownload(recentVideos[selectedVideoIndex])}
                      bg="black"
                      color="rgba(30, 220, 11, 1)"
                      _hover={{ bg: "black" }}
                      size="md"
                      aria-label="Download video"
                    />
                    <IconButton
                      icon={<FaShare />}
                      onClick={() => handleShare(recentVideos[selectedVideoIndex])}
                      bg="black"
                      color="rgba(30, 220, 11, 1)"
                      _hover={{ bg: "black" }}
                      size="md"
                      aria-label="Share video"
                    />
                  </HStack>
                </VStack>

                {/* Desktop Layout: Keep existing overlay design */}
                <Box 
                  display={{ base: 'none', md: 'flex' }}
                  alignItems="center" 
                  justifyContent="center"
                  h="100%"
                >
                  <Box position="relative" maxW="100%" maxH="100%">
                    <video
                      src={recentVideos[selectedVideoIndex].url}
                      controls
                      autoPlay
                      preload="metadata"
                      playsInline
                      style={{ 
                        width: "100%",
                        maxWidth: "80vw",
                        maxHeight: "70vh",
                        borderRadius: "8px",
                        backgroundColor: "#000",
                        display: "block"
                      }}
                      onError={(e) => console.error('Video error:', e)}
                      onLoadStart={() => console.log('Video loading started')}
                      onLoadedData={() => console.log('Video loaded successfully')}
                    />
                    
                    {/* Desktop Navigation buttons */}
                    {recentVideos.length > 1 && (
                      <>
                        <IconButton
                          position="absolute"
                          left={4}
                          top="50%"
                          transform="translateY(-50%)"
                          icon={<FaChevronLeft />}
                          onClick={() => navigateLightbox('prev')}
                          bg="rgba(0, 0, 0, 0.7)"
                          color="white"
                          _hover={{ bg: "rgba(0, 0, 0, 0.9)" }}
                          size="lg"
                          aria-label="Previous video"
                        />
                        <IconButton
                          position="absolute"
                          right={4}
                          top="50%"
                          transform="translateY(-50%)"
                          icon={<FaChevronRight />}
                          onClick={() => navigateLightbox('next')}
                          bg="rgba(0, 0, 0, 0.7)"
                          color="white"
                          _hover={{ bg: "rgba(0, 0, 0, 0.9)" }}
                          size="lg"
                          aria-label="Next video"
                        />
                      </>
                    )}

                    {/* Desktop Action buttons */}
                    <HStack
                      position="absolute"
                      bottom={4}
                      right={4}
                      spacing={2}
                    >
                      <IconButton
                        icon={<FaDownload />}
                        onClick={() => handleDownload(recentVideos[selectedVideoIndex])}
                        bg="rgba(30, 220, 11, 0.8)"
                        color="white"
                        _hover={{ bg: "rgba(30, 220, 11, 1)" }}
                        size="lg"
                        aria-label="Download video"
                      />
                      <IconButton
                        icon={<FaShare />}
                        onClick={() => handleShare(recentVideos[selectedVideoIndex])}
                        bg="rgba(0, 168, 107, 0.8)"
                        color="white"
                        _hover={{ bg: "rgba(0, 168, 107, 1)" }}
                        size="lg"
                        aria-label="Share video"
                      />
                    </HStack>

                    {/* Desktop Video info */}
                    <Box
                      position="absolute"
                      top={4}
                      left={4}
                      bg="rgba(0, 0, 0, 0.8)"
                      p={3}
                      borderRadius="md"
                      maxW="400px"
                    >
                      <Text color="white" fontSize="sm" fontWeight="bold" mb={1}>
                        {recentVideos[selectedVideoIndex].prompt}
                      </Text>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
}

export default VideoGenerate;